import React, { useCallback, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import Menu from "~/ui/menu";

import type { PostType } from "~/__generated__/LibraryChannelPagePostsQuery.graphql";

export type { PostType };
const PARAM_NAME = "contentType" as const;
export const TYPE_TO_WORD: { [key in PostType]: string } = {
	RECORDING: "recordings",
	CLIP: "clips",
	NOTE: "notes",
};

export function usePostTypeFilterQueryParam() {
	const history = useHistory();
	const location = useLocation();

	// Instead of using state, the search param is the source of truth.
	const selectedTypes = useMemo(() => {
		const search = new URLSearchParams(location.search);
		const postTypeParams = search.getAll(PARAM_NAME);
		const typesInSearchParams = new Set<PostType>();

		if (postTypeParams) {
			Object.entries(TYPE_TO_WORD).forEach(([type, word]) => {
				if (!postTypeParams.includes(word)) return;
				typesInSearchParams.add(type as PostType);
			});
		}

		return typesInSearchParams;
	}, [location.search]);

	const changeSelectedTypes = useCallback(
		(action: "ADD" | "REMOVE", type: PostType) => {
			const search = new URLSearchParams(location.search);

			switch (action) {
				case "ADD": {
					search.append(PARAM_NAME, TYPE_TO_WORD[type]);
					break;
				}
				case "REMOVE": {
					// @ts-ignore NOTE: Fixed in Typescript 5
					search.delete(PARAM_NAME, TYPE_TO_WORD[type]);
					break;
				}
			}

			history.replace({
				pathname: location.pathname,
				search: search.toString(),
			});
		},
		[location.search, history, location.pathname],
	);

	return [selectedTypes, changeSelectedTypes] as const;
}

export default function PostTypeFilter() {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const toggleOpen = useCallback(() => setIsOpen(isOpen => !isOpen), []);

	const [selectedTypes, setSelectedTypes] = usePostTypeFilterQueryParam();

	const onCheckedChangeFn = useCallback(
		(type: PostType) => (isEnabled: boolean) => {
			setSelectedTypes(isEnabled ? "ADD" : "REMOVE", type);
		},
		[setSelectedTypes],
	);

	return (
		<Menu.Sub open={isOpen} onOpenChange={toggleOpen}>
			<Menu.SubTrigger>Type</Menu.SubTrigger>
			<Menu.SubContent>
				<Menu.CheckboxItem checked={selectedTypes.has("RECORDING")} onCheckedChange={onCheckedChangeFn("RECORDING")}>
					Recordings
				</Menu.CheckboxItem>
				<Menu.CheckboxItem checked={selectedTypes.has("CLIP")} onCheckedChange={onCheckedChangeFn("CLIP")}>
					Clips
				</Menu.CheckboxItem>
				<Menu.CheckboxItem checked={selectedTypes.has("NOTE")} onCheckedChange={onCheckedChangeFn("NOTE")}>
					Notes
				</Menu.CheckboxItem>
			</Menu.SubContent>
		</Menu.Sub>
	);
}
